import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";
import { Json } from "@/integrations/supabase/types";

// Types
export interface Sequence {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  status: "draft" | "active" | "paused" | "archived";
  channels: string[];
  metadata: Json;
  created_at: string;
  updated_at: string;
  steps?: SequenceStep[];
  enrollments_count?: number;
}

export interface SequenceStep {
  id: string;
  tenant_id: string;
  sequence_id: string;
  position: number;
  channel: "email" | "sms" | "whatsapp" | "linkedin" | "call";
  delay_days: number;
  delay_hours: number;
  subject: string | null;
  content: string;
  metadata: Json;
  created_at: string;
  updated_at: string;
}

export interface MessageTemplate {
  id: string;
  tenant_id: string;
  name: string;
  channel: "email" | "sms" | "whatsapp" | "linkedin" | "call";
  subject: string | null;
  content: string;
  tone: "professional" | "friendly" | "assertive" | "consultative";
  language: string;
  metadata: Json;
  created_at: string;
  updated_at: string;
}

export interface SequenceEnrollment {
  id: string;
  tenant_id: string;
  sequence_id: string;
  lead_id: string | null;
  contact_id: string | null;
  current_step: number;
  status: "active" | "paused" | "completed" | "replied" | "bounced" | "unsubscribed";
  enrolled_at: string;
  last_activity_at: string | null;
  metadata: Json;
  created_at: string;
  updated_at: string;
}

// Sequences hooks
export function useSequences() {
  const { profile } = useAuth();
  
  return useQuery({
    queryKey: ["sequences", profile?.tenant_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sequences")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data as Sequence[];
    },
    enabled: !!profile?.tenant_id,
  });
}

export function useSequenceWithStats() {
  const { profile } = useAuth();
  
  return useQuery({
    queryKey: ["sequences-with-stats", profile?.tenant_id],
    queryFn: async () => {
      // Get sequences
      const { data: sequences, error: seqError } = await supabase
        .from("sequences")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (seqError) throw seqError;

      // Get enrollment counts per sequence
      const { data: enrollments, error: enrollError } = await supabase
        .from("sequence_enrollments")
        .select("sequence_id, status");
      
      if (enrollError) throw enrollError;

      // Calculate stats
      const sequencesWithStats = (sequences as Sequence[]).map(seq => {
        const seqEnrollments = enrollments?.filter(e => e.sequence_id === seq.id) || [];
        return {
          ...seq,
          enrollments_count: seqEnrollments.length,
          active_count: seqEnrollments.filter(e => e.status === "active").length,
          completed_count: seqEnrollments.filter(e => e.status === "completed").length,
          replied_count: seqEnrollments.filter(e => e.status === "replied").length,
        };
      });

      return sequencesWithStats;
    },
    enabled: !!profile?.tenant_id,
  });
}

export function useCreateSequence() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (sequence: { name: string; description: string | null; channels: string[]; status: string; metadata: Json }) => {
      const { data, error } = await supabase
        .from("sequences")
        .insert([{ ...sequence, tenant_id: profile?.tenant_id }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequences"] });
      queryClient.invalidateQueries({ queryKey: ["sequences-with-stats"] });
      toast.success("Sequence created successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to create sequence: ${error.message}`);
    },
  });
}

export function useUpdateSequence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; status?: string; name?: string; description?: string | null; channels?: string[] }) => {
      const { data, error } = await supabase
        .from("sequences")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequences"] });
      queryClient.invalidateQueries({ queryKey: ["sequences-with-stats"] });
      toast.success("Sequence updated successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update sequence: ${error.message}`);
    },
  });
}

export function useDeleteSequence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("sequences")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequences"] });
      queryClient.invalidateQueries({ queryKey: ["sequences-with-stats"] });
      toast.success("Sequence deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete sequence: ${error.message}`);
    },
  });
}

// Message Templates hooks
export function useMessageTemplates() {
  const { profile } = useAuth();
  
  return useQuery({
    queryKey: ["message-templates", profile?.tenant_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("message_templates")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data as MessageTemplate[];
    },
    enabled: !!profile?.tenant_id,
  });
}

export function useCreateMessageTemplate() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (template: { name: string; channel: string; subject: string | null; content: string; tone: string; language: string; metadata: Json }) => {
      const { data, error } = await supabase
        .from("message_templates")
        .insert([{ ...template, tenant_id: profile?.tenant_id }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["message-templates"] });
      toast.success("Template created successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to create template: ${error.message}`);
    },
  });
}

export function useUpdateMessageTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; name?: string; channel?: string; subject?: string | null; content?: string; tone?: string; language?: string }) => {
      const { data, error } = await supabase
        .from("message_templates")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["message-templates"] });
      toast.success("Template updated successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update template: ${error.message}`);
    },
  });
}

export function useDeleteMessageTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("message_templates")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["message-templates"] });
      toast.success("Template deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete template: ${error.message}`);
    },
  });
}
